/**
 * Playwright MCP server registration for pod sessions.
 *
 * Claudespace pods bake `@playwright/mcp` + Chromium into the base image
 * (`Dockerfile.base`) so agents can drive the locally running app in a real
 * browser (`mcp__playwright__*` — see the `playwright-mcp-repro` skill). Two
 * pod-runtime constraints shape the launch flags:
 *
 * - `--no-sandbox`: GKE pods (seccomp RuntimeDefault, non-root, no
 *   unprivileged user namespaces) cannot start Chromium's sandbox — without
 *   this flag every launch dies with `FATAL: No usable sandbox!`.
 * - `--isolated`: a persistent browser profile takes an exclusive lock, which
 *   would collide between the builder session and a same-pod review child.
 *
 * Resolution is PATH-based so environments without the baked binary (dev
 * machines, legacy GitHub Codespaces images) silently skip the registration —
 * the CLI would otherwise fail the spawn and drop ALL MCP servers.
 */
import { findOnPath } from "../harness/pty/adapters/types.js";
import type { ExternalMcpStdioServer } from "../harness/types.js";

/** Current bin name first; `mcp-server-playwright` was the pre-0.0.31 name. */
const PLAYWRIGHT_MCP_BINARIES = ["playwright-mcp", "mcp-server-playwright"];

const PLAYWRIGHT_MCP_ARGS = ["--browser", "chromium", "--headless", "--no-sandbox", "--isolated"];

/**
 * Locate the baked Playwright MCP binary and build its stdio server entry, or
 * null when unavailable on this host.
 */
export function resolvePlaywrightMcpServer(
  env: NodeJS.ProcessEnv = process.env,
): ExternalMcpStdioServer | null {
  for (const binary of PLAYWRIGHT_MCP_BINARIES) {
    const command = findOnPath(binary, env);
    if (command) return { type: "stdio", command, args: [...PLAYWRIGHT_MCP_ARGS] };
  }
  return null;
}
